import React from 'react';
import {
  Box,
  ListItem,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Avatar,
  Link,
} from '@mui/material';

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(num);
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString(undefined, {
    maximumFractionDigits: decimals,
  });
};

const formatPercent = (num) => {
  if (num === null || num === undefined) return '-';
  return `${Number(num).toFixed(2)}%`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

const MetricRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
  </Box>
);

const TokenItemCard = ({ hit }) => {
  const token = hit._source || {};
  const price1h = token.price_diff_pct_1h;
  const price1d = token.price_diff_pct_1d;
  const tokenUrl = token.token_address && token.chain
    ? `https://dexscreener.com/${token.chain}/${token.token_address}`
    : null;

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0,
      }}
    >
      <Card
        elevation={1}
        sx={{
          width: '100%',
          backgroundColor: '#fafafa',
          overflow: 'visible',
        }}
      >
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
              {token.image_url ? (
                <Avatar
                  alt={token.symbol || token.name || 'token'}
                  src={token.image_url}
                  sx={{ width: 40, height: 40, backgroundColor: 'grey.200' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : null}
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {token.name || token.symbol || 'Unknown Token'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  {token.symbol ? <Chip size="small" label={token.symbol} variant="outlined" /> : null}
                  {token.chain ? <Chip size="small" label={token.chain} color="info" /> : null}
                </Box>
              </Box>
            </Box>
            <Chip
              label={hit._score != null ? hit._score.toFixed(2) : '-'}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Box>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <MetricRow label="Price (USD)" value={formatCurrency(token.price_usd)} />
            <MetricRow label="1h Change" value={formatPercent(price1h)} />
            <MetricRow label="1d Change" value={formatPercent(price1d)} />
            <MetricRow label="Market Cap" value={formatCurrency(token.market_cap)} />
            <MetricRow label="Liquidity" value={formatCurrency(token.total_liquidity_usd)} />
            <MetricRow label="Volume (1h)" value={formatCurrency(token.volume_usd_1h)} />
            <MetricRow label="Volume (24h)" value={formatCurrency(token.volume_usd_24h)} />
            <MetricRow label="Holders" value={formatNumber(token.holders_count, 0)} />
            <MetricRow label="Circulating Supply" value={formatNumber(token.circulating_supply, 2)} />
            <MetricRow label="Decimals" value={formatNumber(token.decimals, 0)} />
            <MetricRow label="Last Updated" value={formatDate(token.last_updated)} />
          </Paper>

          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', wordBreak: 'break-all', fontSize: '0.7rem' }}
            >
              {tokenUrl ? (
                <Link href={tokenUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                  {token.token_address || hit._id}
                </Link>
              ) : (
                token.token_address || hit._id
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </ListItem>
  );
};

export default TokenItemCard;
